<script>
import BasicInfo from '@views/NotificationAgentAdmin/form/basic-info.vue'
import DeliveryPeriod from '@views/NotificationAgentAdmin/form/delivery-period.vue'
import DeliveryTarget from '@views/NotificationAgentAdmin/form/delivery-target.vue'
import Preview from '@views/NotificationAgentAdmin/form/preview.vue'

import {
  notificationComputed,
  notificationMethods,
} from '@state/helpers/notification'
import store from '@state/store'
import { NOTIFICATION_STATUS_NAME } from '@utils/constant'
import { FULL_ROLE_AGENT_EDIT } from '@utils/helpers'

export default {
  page: {
    title: 'Notification Agent Admin Details',
    meta: [
      {
        name: 'description',
        content: 'The Notification Agent Admin Details page.',
      },
    ],
  },
  components: {
    BasicInfo,
    DeliveryPeriod,
    DeliveryTarget,
    Preview,
  },
  data() {
    return {
      id: '',
      edit: '',
      reason: '',
      openConfirm: false,
      openEditConfirm: false,
      confirmType: '',
      currentUser: store.getters['auth/getCurrentUser'],
      notificationDetail: {},
    }
  },
  computed: {
    ...notificationComputed,
    canDeleteComputed() {
      return (
        NOTIFICATION_STATUS_NAME.WAITING_FOR_DELIVERY ===
          this.notificationDetail?.status &&
        FULL_ROLE_AGENT_EDIT.includes(this.currentUser.level)
      )
    },
    canDeliverEndComputed() {
      return (
        NOTIFICATION_STATUS_NAME.DELIVERY === this.notificationDetail?.status &&
        FULL_ROLE_AGENT_EDIT.includes(this.currentUser.level)
      )
    },
    canRequestRevokeComputed() {
      return (
        NOTIFICATION_STATUS_NAME.DELIVERY === this.notificationDetail?.status &&
        FULL_ROLE_AGENT_EDIT.includes(this.currentUser.level)
      )
    },
  },
  created() {
    this.init()
    const user = this.$store.getters['auth/getCurrentUser']
    this.$router
      .replace({
        query: { agent_code: user?.agent?.agent_code },
      }) // eslint-disable-next-line handle-callback-err
      .catch((error) => {})
  },
  methods: {
    ...notificationMethods,
    async init() {
      this.edit = ''
      const id = this.$route.params.id || 0
      this.id = id
      const response = await this.fetchingNotificationById({ id })
      if (response) {
        const notificationDetail = response || {}
        const filterObj = notificationDetail.filter_obj || null
        if (filterObj) {
          const keys = [
            'age_range_lower',
            'age_range_upper',
            'weight_differ_lower',
            'weight_differ_upper',
            'body_fat_rate_differ_lower',
            'body_fat_rate_differ_upper',
          ]
          for (const key of keys) {
            if (filterObj[key] === -9999) {
              filterObj[key] = ''
            }
          }
          notificationDetail.filter_obj = filterObj
        }
        this.notificationDetail = notificationDetail
      }
    },
    getType(name) {
      if (this.edit === name) {
        return 'edit'
      }
      return 'view'
    },
    getTriggerMask(name) {
      return !(this.edit === '' || this.edit === name)
    },
    handleEdit(name, value) {
      if (value) {
        this.edit = name
      } else {
        this.init()
      }
    },
    async handleActionByStatus(action) {
      this.confirmType = action
      if (action === 'request_revoke') {
        this.openConfirm = false
        this.openEditConfirm = true
        this.reason = ''
      } else {
        this.openEditConfirm = false
        this.openConfirm = true
      }
    },
    async handleSubmit() {
      const { id } = this.notificationDetail
      let response = null
      switch (this.confirmType) {
        case 'delete':
          response = await this.deleteNotification({ id })
          if (response) {
            await this.$router.push({ name: 'notificationAgentAdmin' })
            return
          }
          break
        case 'deliver_end':
          response = await this.deliverEndNotification({ id })
          break
        case 'request_revoke_confirm':
          response = await this.requestRevoke({ id, reason: this.reason })
          break
      }
      if (response) {
        await this.init()
      }
      this.openConfirm = false
      this.openEditConfirm = false
    },
    async handleSubmitEdit(data) {
      const notificationDetail = { ...this.notificationDetail, ...data }
      if (!notificationDetail.order) {
        notificationDetail.order = 99
      }
      if (notificationDetail.image_binary) {
        const imageBinary = notificationDetail.image_binary
        notificationDetail.image_binary = imageBinary.includes(',')
          ? imageBinary.split(',')[1]
          : ''
      }
      if (notificationDetail.filter_obj) {
        const keys = [
          'age_range_lower',
          'age_range_upper',
          'weight_differ_lower',
          'weight_differ_upper',
          'body_fat_rate_differ_lower',
          'body_fat_rate_differ_upper',
        ]
        const filterObj = notificationDetail.filter_obj
        for (const key of keys) {
          if (!filterObj[key] && filterObj[key] !== 0) {
            filterObj[key] = -9999
          }
        }
        notificationDetail.filter_obj = filterObj
      } else {
        notificationDetail.filter_obj = null
      }

      const response = await this.editNotification({
        ...notificationDetail,
        ...{
          agent_id: this.currentUser?.agent.id,
          place: 1,
          updated_admin_id: this.currentUser?.id,
        },
      })
      if (response) {
        this.init()
      }
    },
  },
}
</script>

<template>
  <div id="notification-details">
    <CRow>
      <CCol sm="6">
        <BasicInfo
          :notification-detail="notificationDetail"
          :type="getType('BasicInfo')"
          :trigger-mask="getTriggerMask('BasicInfo')"
          @enable:edit="(value) => handleEdit('BasicInfo', value)"
          @edit:notification="handleSubmitEdit"
        />
        <DeliveryPeriod
          :notification-detail="notificationDetail"
          :type="getType('DeliveryPeriod')"
          :trigger-mask="getTriggerMask('DeliveryPeriod')"
          @enable:edit="(value) => handleEdit('DeliveryPeriod', value)"
          @edit:notification="handleSubmitEdit"
        />
        <DeliveryTarget
          :notification-detail="notificationDetail"
          :type="getType('DeliveryTarget')"
          :trigger-mask="getTriggerMask('DeliveryTarget')"
          @enable:edit="(value) => handleEdit('DeliveryTarget', value)"
          @edit:notification="handleSubmitEdit"
        />
      </CCol>
      <CCol sm="6">
        <Preview
          :type="edit === '' ? 'view' : 'edit'"
          :notification-detail="notificationDetail"
        />
        <div class="d-flex justify-content-end">
          <CButton
            v-if="canRequestRevokeComputed"
            class="mr-2"
            color="dark"
            variant="outline"
            @click="handleActionByStatus('request_revoke')"
          >
            無効化申請
          </CButton>
          <CButton
            v-if="canDeliverEndComputed"
            color="dark"
            variant="outline"
            @click="handleActionByStatus('deliver_end')"
          >
            配信終了
          </CButton>
          <CButton
            v-if="canDeleteComputed"
            color="dark"
            variant="outline"
            @click="handleActionByStatus('delete')"
          >
            削除
          </CButton>
        </div>
      </CCol>
    </CRow>
    <BaseModalConfirm
      v-if="openConfirm"
      backdrop-close
      :show.sync="openConfirm"
      :type="confirmType"
      @closeModal="openConfirm = false"
      @submit="handleSubmit"
    />
    <BaseFormModal
      v-if="openEditConfirm"
      :show-modal="openEditConfirm"
      :type="confirmType"
      @close="openEditConfirm = false"
      @submit="handleActionByStatus('request_revoke_confirm')"
    >
      <template v-slot:form>
        <BaseInput
          v-model="reason"
          placeholder="100文字以内で入力してください"
          :rules="{ required: true, max: 100 }"
        />
      </template>
    </BaseFormModal>
  </div>
</template>
